import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import AutoGrowingTextarea from './AutoGrowingTextarea';

// ─── Markdown cleaner ────────────────────────────────────────────────────────
export const stripMarkdown = (text) => {
    if (!text || typeof text !== 'string') return text ?? '';
    return text
        .replace(/\*\*(.*?)\*\*/g, '$1')   // **bold**
        .replace(/\*(.*?)\*/g, '$1')        // *italic*
        .replace(/^[-*•]\s+/gm, '')         // bullet markers
        .replace(/^#{1,6}\s+/gm, '')        // headings
        .replace(/`([^`]+)`/g, '$1');       // inline code
};

// ─── Shared styles ───────────────────────────────────────────────────────────
const addBtn = {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: 6, width: '100%', padding: '9px 0',
    color: '#FF5526', background: 'transparent',
    border: '1.5px dashed #FFD0C2', borderRadius: 8,
    cursor: 'pointer', fontSize: 13, fontWeight: 600,
    marginTop: 4, transition: 'all 0.15s',
};

const cardBase = (readOnly) => ({
    background: '#fff',
    border: '1px solid #E8EDF2',
    borderRadius: 10,
    padding: '12px 14px 10px 14px',
    position: 'relative',
    transition: 'box-shadow 0.15s',
    ...(readOnly ? {} : { ':hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.06)' } })
});

const titleInp = (readOnly) => ({
    fontWeight: 600, fontSize: 13, color: '#1E293B',
    border: readOnly ? 'none' : '1px solid #E2E8F0',
    outline: 'none',
    background: readOnly ? 'transparent' : '#FFFFFF',
    width: '100%',
    padding: readOnly ? 0 : '8px 12px',
    borderRadius: readOnly ? 0 : 8,
    transition: 'all 0.15s ease',
    ...(readOnly ? {} : { cursor: 'text' })
});

const contentArea = (readOnly) => ({
    fontSize: 13, color: '#475569', lineHeight: 1.6,
    border: readOnly ? 'none' : '1px solid #E2E8F0',
    outline: 'none',
    background: readOnly ? 'transparent' : '#FFFFFF',
    width: '100%',
    padding: readOnly ? 0 : '8px 12px',
    borderRadius: readOnly ? 0 : 8,
    resize: 'none',
    marginTop: readOnly ? 2 : 8,
    transition: 'all 0.15s ease',
    ...(readOnly ? {} : { cursor: 'text' })
});

const trashBtn = {
    position: 'absolute', top: 10, right: 10,
    background: 'none', border: 'none', cursor: 'pointer',
    color: '#CBD5E1', padding: 2, borderRadius: 4,
    display: 'flex', alignItems: 'center', lineHeight: 1,
    transition: 'color 0.15s',
};

// ─── Card-based editor (Pyramid, Venn, Spotlight, Q&A, etc.) ─────────────────
export const CardListEditor = ({
    value = [], onChange, readOnly,
    titleKey, contentKey,
    titlePlaceholder = 'Title', contentPlaceholder = 'Description',
    addLabel = 'Add Item',
}) => {
    const rows = Array.isArray(value) ? value : [];
    const update = (idx, key, val) => onChange(rows.map((r, i) => i === idx ? { ...r, [key]: val } : r));
    const addRow = () => onChange([...rows, { [titleKey]: '', [contentKey]: '' }]);
    const removeRow = (idx) => onChange(rows.filter((_, i) => i !== idx));

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {rows.map((row, idx) => (
                <div
                    key={idx}
                    style={cardBase(readOnly)}
                    onMouseEnter={e => { if (!readOnly) e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)'; }}
                    onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}
                >
                    {/* Title row */}
                    <div style={{ display: 'flex', alignItems: 'flex-start', paddingRight: !readOnly ? 24 : 0 }}>
                        <input
                            disabled={readOnly}
                            value={stripMarkdown(row[titleKey] ?? '')}
                            onChange={e => update(idx, titleKey, e.target.value)}
                            placeholder={titlePlaceholder}
                            style={titleInp(readOnly)}
                        />
                    </div>

                    {/* Separator */}
                    {readOnly && <div style={{ height: 1, background: '#F1F5F9', margin: '8px 0' }} />}

                    {/* Content */}
                    <textarea
                        disabled={readOnly}
                        value={stripMarkdown(row[contentKey] ?? '')}
                        onChange={e => update(idx, contentKey, e.target.value)}
                        placeholder={contentPlaceholder}
                        rows={2}
                        style={contentArea(readOnly)}
                    />

                </div>
            ))}
        </div>
    );
};

// ─── Multi-field card (Chevron, Timeline, Journey, Funnel) ───────────────────
const MultiFieldCard = ({ row, idx, fields, readOnly, onChange, onRemove, showRemove }) => (
    <div
        style={cardBase(readOnly)}
        onMouseEnter={e => { if (!readOnly) e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)'; }}
        onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}
    >
        {/* Step badge */}
        <div style={{ fontSize: 10, fontWeight: 700, color: '#94A3B8', letterSpacing: '0.06em', marginBottom: 8 }}>STEP {idx + 1}</div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {fields.map((f, fi) => (
                <div key={f.key}>
                    {fi > 0 && <div style={{ height: 1, background: '#F1F5F9', marginBottom: 6 }} />}
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#94A3B8', letterSpacing: '0.05em', marginBottom: 3 }}>
                        {f.label.toUpperCase()}
                    </div>
                    {f.multiline ? (
                        <AutoGrowingTextarea
                            disabled={readOnly}
                            value={stripMarkdown(row[f.key] ?? '')}
                            onChange={e => onChange(f.key, e.target.value)}
                            rows={2}
                            placeholder={f.placeholder || f.label}
                            style={contentArea(readOnly)}
                        />
                    ) : (
                        <input
                            disabled={readOnly}
                            value={stripMarkdown(row[f.key] ?? '')}
                            onChange={e => onChange(f.key, e.target.value)}
                            placeholder={f.placeholder || f.label}
                            style={titleInp(readOnly)}
                        />
                    )}
                </div>
            ))}
        </div>
    </div>
);

const RowListEditor = ({ value, onChange, readOnly, fields, addLabel = 'Add Item' }) => {
    const rows = Array.isArray(value) ? value : [];
    const update = (idx, key, val) => onChange(rows.map((r, i) => i === idx ? { ...r, [key]: val } : r));
    const addRow = () => onChange([...rows, fields.reduce((a, f) => ({ ...a, [f.key]: '' }), {})]);
    const removeRow = (idx) => onChange(rows.filter((_, i) => i !== idx));

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {rows.map((row, idx) => (
                <MultiFieldCard
                    key={idx}
                    row={row}
                    idx={idx}
                    fields={fields}
                    readOnly={readOnly}
                    onChange={(key, val) => update(idx, key, val)}
                    onRemove={() => removeRow(idx)}
                    showRemove={rows.length > 1}
                />
            ))}
        </div>
    );
};

// ─── Archetype-specific exports ──────────────────────────────────────────────

export const StageActionEditor = ({ value, onChange, readOnly }) => (
    <RowListEditor value={value} onChange={onChange} readOnly={readOnly} addLabel="Add Stage"
        fields={[
            { key: 'stage', label: 'Stage', placeholder: 'e.g., Phase 1: Discovery' },
            { key: 'action', label: 'Action', placeholder: 'e.g., Align tech capabilities...', multiline: true },
        ]} />
);

export const TimelineEditor = ({ value, onChange, readOnly }) => (
    <RowListEditor value={value} onChange={onChange} readOnly={readOnly} addLabel="Add Milestone"
        fields={[
            { key: 'weeks', label: 'Period', placeholder: 'e.g., Week 1–2' },
            { key: 'title', label: 'Title', placeholder: 'e.g., Project Kickoff' },
            { key: 'description', label: 'Description', placeholder: 'e.g., Configure dev pipelines.', multiline: true },
        ]} />
);

export const JourneyEditor = ({ value, onChange, readOnly }) => (
    <RowListEditor value={value} onChange={onChange} readOnly={readOnly} addLabel="Add Journey Step"
        fields={[
            { key: 'top_text', label: 'Phase', placeholder: 'e.g., Phase 01' },
            { key: 'bottom_text', label: 'Description', placeholder: 'e.g., Establish repository templates.', multiline: true },
        ]} />
);

export const DetailedJourneyEditor = ({ value, onChange, readOnly }) => (
    <RowListEditor value={value} onChange={onChange} readOnly={readOnly} addLabel="Add Step"
        fields={[
            { key: 'top_label', label: 'Label', placeholder: 'e.g., 01' },
            { key: 'header', label: 'Title', placeholder: 'e.g., Initiate' },
            { key: 'content', label: 'Description', placeholder: 'e.g., Establish project guidelines...', multiline: true },
        ]} />
);

export const StageDescriptionEditor = ({ value, onChange, readOnly }) => (
    <CardListEditor
        value={value} onChange={onChange} readOnly={readOnly}
        titleKey="stage" contentKey="description"
        titlePlaceholder="Stage name" contentPlaceholder="Stage description"
        addLabel="Add Stage"
    />
);

// Pyramid, Venn → clean card layout (title + content)
export const HeaderContentListEditor = ({ value, onChange, readOnly }) => (
    <CardListEditor
        value={value} onChange={onChange} readOnly={readOnly}
        titleKey="header" contentKey="content"
        titlePlaceholder="Title" contentPlaceholder="Description"
        addLabel="Add Item"
    />
);

// Spotlight → card layout (headline + subtext)
export const HeadlineSubtextEditor = ({ value, onChange, readOnly }) => (
    <CardListEditor
        value={value} onChange={onChange} readOnly={readOnly}
        titleKey="headline" contentKey="subtext"
        titlePlaceholder="Headline" contentPlaceholder="Supporting text"
        addLabel="Add Spotlight Item"
    />
);

export const QAEditor = ({ value, onChange, readOnly }) => (
    <CardListEditor
        value={value} onChange={onChange} readOnly={readOnly}
        titleKey="question" contentKey="answer"
        titlePlaceholder="Question" contentPlaceholder="Answer"
        addLabel="Add Q&A Pair"
    />
);

export const CaseStudyEditor = ({ value, onChange, readOnly }) => (
    <RowListEditor value={value} onChange={onChange} readOnly={readOnly} addLabel="Add Case Study"
        fields={[
            { key: 'client', label: 'Client', placeholder: 'Client Name' },
            { key: 'obstacle', label: 'Challenge', placeholder: 'e.g., Consultants spent 40h per RFP.', multiline: true },
            { key: 'solution', label: 'Solution', placeholder: 'e.g., Deployed the VelocityRFP system.', multiline: true },
            { key: 'result', label: 'Result', placeholder: 'e.g., Reduced time to under 5 minutes.', multiline: true },
        ]} />
);
